/**
 * NotificationsList — the lower Inbox lane (Inbox V2, spec §4.1/§4.3).
 *
 * No-action FYI bullets. DATE-GROUPED (Today / Yesterday / Jun 19), VIRTUALIZED
 * (@tanstack/react-virtual — already a dep) so a year of bullets never blows the
 * DOM, and INFINITE-SCROLL via a cursor sentinel (keyset pagination handled by
 * the caller through `onLoadMore`). Each row is a terse "·" bullet + body_preview
 * + the viewer-local time; tap → source card. Dismiss: hover-X (desktop) /
 * swipe-left (mobile).
 *
 * The list is rendered as a flat sequence of {group-header | row} entries so the
 * virtualizer measures both. Group headers are sticky-feeling via the same
 * absolute-translateY layout used by list-view.tsx.
 *
 * ADR-057: monochrome, semantic tokens only. English only.
 */
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader2, X } from 'lucide-react';
import * as React from 'react';

import { env } from '../../env';
import type { Mention } from '../../lib/api/mentions';
import { cn } from '../../lib/utils';
import { dateBucketLabel, dateGroupKey, formatLocalTime } from '../../lib/time-group';
import { useIsMobile } from '../../lib/use-media-query';
import { resolveInboxLink, useCardScope, useInboxNavigate } from './inbox-shared';
import { OrgBadge } from './org-badge';

const GROUP_HEADER_HEIGHT = 28;
// Light system (I5): date-group headers lose their tinted plate and gain 16px of
// air above the group, so the header cell is taller (label sits at the bottom).
const GROUP_HEADER_HEIGHT_LIGHT = 44;
const ROW_HEIGHT = 44; // 44px min tap target (mobile).
const SENTINEL_HEIGHT = 40;

type Entry =
  | { kind: 'group'; key: string; label: string }
  | { kind: 'row'; mention: Mention }
  | { kind: 'sentinel' };

interface NotificationsListProps {
  items: Mention[];
  isLoading: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  onDismiss: (id: string) => void;
  onNavigate?: () => void;
  /** Empty-state copy (reuses the bell empty-state by default). */
  emptyText?: string;
  /**
   * I7/D5 un-nest (light only): when set, the list stops being its own bounded
   * `overflow-y-auto` scroller and virtualizes against this OUTER scroll element
   * (the shared InboxSplit scroller) instead — so the whole Inbox is ONE scroll,
   * killing the nested scrollbars that collapse in landscape. Left undefined on
   * an OFF build (and pre-mount, before the callback ref resolves), where the
   * list keeps its proven self-scroller behaviour.
   */
  scrollParent?: HTMLElement | null;
}

export function NotificationsList({
  items,
  isLoading,
  hasMore,
  isFetchingMore,
  onLoadMore,
  onDismiss,
  onNavigate,
  emptyText = "No notifications. When agents post updates, they'll appear here.",
  scrollParent,
}: NotificationsListProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const now = React.useMemo(() => new Date(), []);

  // I7/D5 (light): virtualize against the OUTER shared scroller when one is
  // provided so the Inbox is a single scroll (no nested scrollbars). OFF path
  // keeps the self-scroller (`scrollRef`). `parentMode` is dead when the flag is
  // off (scrollParent is never passed).
  const parentMode = env.VITE_DESIGN_LIGHT && scrollParent !== undefined;
  const groupHeaderHeight = env.VITE_DESIGN_LIGHT ? GROUP_HEADER_HEIGHT_LIGHT : GROUP_HEADER_HEIGHT;

  // In parent-scroll mode the virtualizer needs `scrollMargin` = the offset of
  // this list's container from the top of the shared scroller's content (the
  // Needs-You lane sits above it). Measured live so it tracks Needs-You loading
  // and viewport resize.
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = React.useState(0);
  React.useLayoutEffect(() => {
    if (!parentMode) return;
    const el = containerRef.current;
    const sc = scrollParent ?? null;
    if (!el || !sc) return;
    const measure = () => {
      const next =
        el.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop;
      setScrollMargin((prev) => (Math.abs(prev - next) > 0.5 ? Math.max(next, 0) : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(sc);
    ro.observe(el);
    return () => ro.disconnect();
  });

  // Flatten into [group, ...rows, group, ...rows, (sentinel)].
  const flat: Entry[] = React.useMemo(() => {
    const out: Entry[] = [];
    let lastKey: string | null = null;
    for (const m of items) {
      const key = dateGroupKey(m.createdAt, now);
      if (key !== lastKey) {
        out.push({ kind: 'group', key, label: dateBucketLabel(m.createdAt, now) });
        lastKey = key;
      }
      out.push({ kind: 'row', mention: m });
    }
    if (hasMore) out.push({ kind: 'sentinel' });
    return out;
  }, [items, hasMore, now]);

  const virtualiser = useVirtualizer({
    count: flat.length,
    getScrollElement: () => (parentMode ? (scrollParent ?? null) : scrollRef.current),
    estimateSize: (i) => {
      const e = flat[i];
      if (e?.kind === 'group') return groupHeaderHeight;
      if (e?.kind === 'sentinel') return SENTINEL_HEIGHT;
      return ROW_HEIGHT;
    },
    overscan: 10,
    scrollMargin: parentMode ? scrollMargin : 0,
  });

  // Infinite scroll: when the sentinel entry becomes virtual (near the bottom),
  // fire onLoadMore once per page. Guarded by hasMore + isFetchingMore.
  const vItems = virtualiser.getVirtualItems();
  React.useEffect(() => {
    if (!hasMore || isFetchingMore) return;
    const sentinelIndex = flat.length - 1;
    if (flat[sentinelIndex]?.kind !== 'sentinel') return;
    const lastVisible = vItems[vItems.length - 1];
    if (lastVisible && lastVisible.index >= sentinelIndex - 1) {
      onLoadMore();
    }
  }, [vItems, hasMore, isFetchingMore, flat, onLoadMore]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground md:px-6">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return <p className="px-4 py-6 text-[13px] text-muted-foreground md:px-6">{emptyText}</p>;
  }

  return (
    // Parent-scroll mode (light): NOT a scroller — a plain relative container the
    // shared InboxSplit scroller virtualizes against. Self mode (OFF): its own
    // bounded `overflow-y-auto` scroller (proven behaviour).
    <div
      ref={parentMode ? containerRef : scrollRef}
      className={cn('relative', !parentMode && 'flex-1 overflow-y-auto')}
      data-testid="notifications-list"
    >
      <div style={{ height: `${virtualiser.getTotalSize()}px`, width: '100%', position: 'relative' }}>
        {vItems.map((vrow) => {
          const entry = flat[vrow.index];
          if (!entry) return null;
          const style: React.CSSProperties = {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            // In parent-scroll mode `start` already includes scrollMargin — offset
            // it back so rows sit at 0 within this (already offset) container.
            transform: `translateY(${vrow.start - (parentMode ? scrollMargin : 0)}px)`,
          };
          if (entry.kind === 'group') {
            return (
              <div
                key={`g-${entry.key}`}
                style={style}
                className={
                  env.VITE_DESIGN_LIGHT
                    ? // I5: no tinted plate, sentence case, 16px air above the group.
                      'flex h-11 items-end px-4 pb-1 text-[12px] font-medium text-muted-foreground md:px-6'
                    : 'flex h-7 items-center bg-foreground/[0.03] px-4 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground md:px-6'
                }
              >
                {entry.label}
              </div>
            );
          }
          if (entry.kind === 'sentinel') {
            return (
              <div
                key="sentinel"
                style={style}
                className="flex items-center justify-center px-4 text-[12px] text-muted-foreground md:px-6"
              >
                {isFetchingMore ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Loading…
                  </span>
                ) : null}
              </div>
            );
          }
          return (
            <div key={entry.mention.id} style={style}>
              <NotificationRow
                mention={entry.mention}
                onDismiss={onDismiss}
                onNavigate={onNavigate}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface NotificationRowProps {
  mention: Mention;
  onDismiss: (id: string) => void;
  onNavigate?: () => void;
}

function NotificationRow({ mention, onDismiss, onNavigate }: NotificationRowProps) {
  const inboxNavigate = useInboxNavigate();
  const isMobile = useIsMobile();
  const [swipeX, setSwipeX] = React.useState(0);
  const touchStart = React.useRef<number | null>(null);

  const text = mention.bodyPreview ?? mention.title ?? '';

  // Resolve the task (card) name for this row so a notification reads
  // "org · #task · what happened", consistent with the Needs You lane. Cached +
  // enabled-gated in useCardScope, so only the visible virtualized rows fetch;
  // the org name is shown by OrgBadge, so we only need the card title here.
  const scope = useCardScope(mention.cardId, mention.orgId, !!mention.cardId);
  const taskCrumb = scope.cardTitle ? `#${scope.cardTitle}` : null;
  // I2 (light): fold the org into the crumb as plain muted text (`Org · #Card`)
  // instead of a bordered OrgBadge chip.
  const lightCrumb = [scope.orgName, taskCrumb].filter(Boolean).join(' · ');

  const handleClick = async (): Promise<void> => {
    const target = await resolveInboxLink(mention);
    onNavigate?.();
    if (!target) return;
    // The source card may be in a NON-active org — switch context first.
    await inboxNavigate(target, mention.orgId);
  };

  // Swipe-left to dismiss (mobile). Threshold = 80px.
  const onTouchStart = (e: React.TouchEvent): void => {
    touchStart.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchMove = (e: React.TouchEvent): void => {
    if (touchStart.current === null) return;
    const dx = (e.touches[0]?.clientX ?? 0) - touchStart.current;
    if (dx < 0) setSwipeX(Math.max(dx, -120));
  };
  const onTouchEnd = (): void => {
    if (swipeX <= -80) {
      onDismiss(mention.id);
    }
    setSwipeX(0);
    touchStart.current = null;
  };

  return (
    <div
      className="group/notif relative flex items-center"
      style={{ height: ROW_HEIGHT }}
      {...(isMobile
        ? { onTouchStart, onTouchMove, onTouchEnd }
        : {})}
    >
      <button
        type="button"
        onClick={() => void handleClick()}
        data-testid="notification-row"
        className="flex h-full min-w-0 flex-1 items-center gap-2 px-4 text-left transition-colors hover:bg-foreground/5 active:bg-foreground/10 md:px-6"
        style={isMobile ? { transform: `translateX(${swipeX}px)` } : undefined}
      >
        {env.VITE_DESIGN_LIGHT ? (
          // Light: no fake "·" list marker (I6), no OrgBadge chip — the org +
          // task fold into one plain muted crumb (I2). Hidden on the narrowest
          // widths so the "what happened" text keeps priority.
          lightCrumb ? (
            <span className="hidden shrink-0 truncate text-[11px] text-muted-foreground sm:inline-block sm:max-w-[45%]">
              {lightCrumb}
            </span>
          ) : null
        ) : (
          <>
            <span aria-hidden className="shrink-0 text-muted-foreground">
              ·
            </span>
            {/* Org badge — which org this notification is from (cross-org Inbox). */}
            <OrgBadge orgId={mention.orgId} className="shrink-0" />
            {/* Task (card) name — mirrors the Needs You breadcrumb so a notification
                reads "org · #task · what happened", not a bare machine line. */}
            {taskCrumb ? (
              <span className="hidden shrink-0 truncate text-[11px] text-muted-foreground sm:inline-block sm:max-w-[34%]">
                {taskCrumb}
              </span>
            ) : null}
          </>
        )}
        {/* Plain-language "what happened". */}
        <span className="min-w-0 flex-1 truncate text-[12.5px] text-foreground">{text}</span>
        <time
          dateTime={mention.createdAt}
          className="shrink-0 text-[11px] tabular-nums text-muted-foreground"
        >
          {formatLocalTime(mention.createdAt)}
        </time>
      </button>
      {/* Desktop: hover-X dismiss. */}
      {!isMobile && (
        <button
          type="button"
          onClick={() => onDismiss(mention.id)}
          aria-label="Dismiss notification"
          data-testid="notification-dismiss"
          className={cn(
            'mr-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-foreground/10 hover:text-foreground',
            'group-hover/notif:opacity-100 focus:opacity-100',
          )}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
}
